/**
 * HTTP payment service — gateway `/payments` endpoints (taxhive-payment-service).
 *
 * Path map (gateway base URL already includes `/api/v1`):
 *   list           GET    /payments
 *   listForCustomer GET   /payments/customer/{id}
 *   get            GET    /payments/{id}
 *   create         POST   /payments
 *   updateStatus   PATCH  /payments/{id}/status
 *   process        POST   /payments/{id}/process?transaction_id=...
 *   fail           POST   /payments/{id}/fail?reason=...
 *   getStats       GET    /payments/summary/stats
 */

import { apiClient, toServiceError } from '@/services/http/client'
import type { PaymentService } from './payments.service'
import type {
  Payment,
  PaymentStats,
  CreatePaymentInput,
  UpdatePaymentInput,
  OnlinePaymentInput,
  RazorpayCheckoutOrder,
  RazorpayCheckoutProof,
  RazorpaySubscriptionCheckoutOrder,
  RazorpaySubscriptionPaymentResult,
} from '@/types/payment.types'
import {
  mapPaymentFromApi,
  mapPaymentListFromApi,
  mapPaymentStatsFromApi,
  mapPaymentCreateToApi,
  mapPaymentUpdateToApi,
  mapOnlinePaymentToApi,
  mapCheckoutOrderFromApi,
  mapCheckoutProofToApi,
  mapProviderPaymentResultFromApi,
  mapSubscriptionCheckoutOrderFromApi,
  mapSubscriptionPaymentResultFromApi,
} from './mappers'

const PAGE_SIZE = 100

export function createHttpPaymentService(): PaymentService {
  return {
    async list(): Promise<Payment[]> {
      try {
        const res = await apiClient.get('/payments', {
          params: { page: 1, page_size: PAGE_SIZE },
        })
        return mapPaymentListFromApi(res.data)
      } catch (err) {
        throw toServiceError(err)
      }
    },

    async listForCustomer(customerId: string): Promise<Payment[]> {
      try {
        const res = await apiClient.get(`/payments/customer/${customerId}`, {
          params: { page: 1, page_size: PAGE_SIZE },
        })
        return mapPaymentListFromApi(res.data)
      } catch (err) {
        throw toServiceError(err)
      }
    },

    async get(id: string): Promise<Payment> {
      try {
        const res = await apiClient.get(`/payments/${id}`)
        return mapPaymentFromApi(res.data)
      } catch (err) {
        throw toServiceError(err)
      }
    },

    async create(input: CreatePaymentInput): Promise<Payment> {
      try {
        const res = await apiClient.post('/payments', mapPaymentCreateToApi(input))
        return mapPaymentFromApi(res.data)
      } catch (err) {
        throw toServiceError(err)
      }
    },

    async createOnlineOrder(
      input: OnlinePaymentInput,
      idempotencyKey: string
    ): Promise<RazorpayCheckoutOrder> {
      try {
        const res = await apiClient.post(
          '/payments/orders',
          mapOnlinePaymentToApi(input),
          { headers: { 'Idempotency-Key': idempotencyKey } }
        )
        return mapCheckoutOrderFromApi(res.data)
      } catch (err) {
        throw toServiceError(err)
      }
    },

    async verifyOnlinePayment(proof: RazorpayCheckoutProof): Promise<Payment> {
      try {
        const res = await apiClient.post(
          '/payments/verify',
          mapCheckoutProofToApi(proof)
        )
        return mapProviderPaymentResultFromApi(res.data)
      } catch (err) {
        throw toServiceError(err)
      }
    },

    async reconcile(id: string): Promise<Payment> {
      try {
        const res = await apiClient.get(`/payments/${id}/provider-status`)
        return mapProviderPaymentResultFromApi(res.data)
      } catch (err) {
        throw toServiceError(err)
      }
    },

    async createSubscriptionOrder(
      invoiceId: string,
      idempotencyKey: string
    ): Promise<RazorpaySubscriptionCheckoutOrder> {
      try {
        const res = await apiClient.post(
          '/payments/subscription-orders',
          { invoice_id: invoiceId },
          { headers: { 'Idempotency-Key': idempotencyKey } }
        )
        return mapSubscriptionCheckoutOrderFromApi(res.data)
      } catch (err) {
        throw toServiceError(err)
      }
    },

    async verifySubscriptionPayment(
      proof: RazorpayCheckoutProof
    ): Promise<RazorpaySubscriptionPaymentResult> {
      try {
        const res = await apiClient.post(
          '/payments/subscription-verify',
          mapCheckoutProofToApi(proof)
        )
        return mapSubscriptionPaymentResultFromApi(res.data)
      } catch (err) {
        throw toServiceError(err)
      }
    },

    async updateStatus(id: string, updates: UpdatePaymentInput): Promise<Payment> {
      try {
        const res = await apiClient.patch(
          `/payments/${id}/status`,
          mapPaymentUpdateToApi(updates)
        )
        return mapPaymentFromApi(res.data)
      } catch (err) {
        throw toServiceError(err)
      }
    },

    async process(id: string, transactionId: string): Promise<Payment> {
      try {
        const res = await apiClient.post(`/payments/${id}/process`, null, {
          params: { transaction_id: transactionId },
        })
        return mapPaymentFromApi(res.data)
      } catch (err) {
        throw toServiceError(err)
      }
    },

    async fail(id: string, reason: string): Promise<Payment> {
      try {
        const res = await apiClient.post(`/payments/${id}/fail`, null, {
          params: { reason },
        })
        return mapPaymentFromApi(res.data)
      } catch (err) {
        throw toServiceError(err)
      }
    },

    async getStats(): Promise<PaymentStats> {
      try {
        const res = await apiClient.get('/payments/summary/stats')
        return mapPaymentStatsFromApi(res.data)
      } catch (err) {
        throw toServiceError(err)
      }
    },
  }
}
